import { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import FilterBar from "@/components/admin/FilterBar";
import QuickDateChips from "@/components/admin/QuickDateChips";
import StatusDropdown from "@/components/admin/StatusDropdown";
import ReminderButton from "@/components/admin/ReminderButton";
import PaymentBadge from "@/components/admin/PaymentBadge";
import { exportCsv } from "@/lib/csv";
import { toast } from "sonner";
import {
    BOOKING_FILTER_FIELDS_OPTIONS,
    EMPTY_BOOKING_FILTERS,
    matchesText,
    withinDateRange,
} from "@/components/admin/adminFilters";

export default function BookingsTab({ bookings, onChanged }) {
    const [filters, setFilters] = useState(EMPTY_BOOKING_FILTERS);

    const filtered = useMemo(() => {
        const f = filters;
        return bookings.filter((b) => {
            if (
                !matchesText(
                    [b.full_name, b.email, b.phone, b.reference_id, b.moving_to, b.moving_from],
                    f.q
                )
            )
                return false;
            if (f.service_key && b.service_key !== f.service_key) return false;
            if (f.status && b.status !== f.status) return false;
            if (f.payment_status && b.payment_status !== f.payment_status) return false;
            if (
                (f.date_from || f.date_to) &&
                !withinDateRange(b.preferred_date, f.date_from, f.date_to)
            )
                return false;
            return true;
        });
    }, [bookings, filters]);

    function exportRows() {
        const headers = [
            { key: "reference_id", label: "Reference" },
            { key: "service_label", label: "Service" },
            { key: "service_key", label: "Service key" },
            { key: "status", label: "Status" },
            { key: "payment_status", label: "Payment status" },
            { key: "amount", label: "Amount" },
            { key: "currency", label: "Currency" },
            { key: "full_name", label: "Client name" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "preferred_date", label: "Preferred date" },
            { key: "preferred_time", label: "Preferred time" },
            { key: "moving_from", label: "Moving from" },
            { key: "moving_to", label: "Moving to" },
            { key: "expected_move_date", label: "Expected move date" },
            { key: "message", label: "Client message" },
            { key: "admin_notes", label: "Admin notes" },
            { key: "created_at", label: "Created at" },
        ];
        exportCsv(`bookings_${new Date().toISOString().slice(0, 10)}.csv`, headers, filtered);
        toast.success(`Exported ${filtered.length} booking(s)`);
    }

    return (
        <>
            <div className="mt-6">
                <QuickDateChips value={filters} onChange={setFilters} />
                <FilterBar
                    fields={[
                        { key: "q", label: "Search", type: "text" },
                        {
                            key: "service_key",
                            label: "Service",
                            type: "select",
                            options: BOOKING_FILTER_FIELDS_OPTIONS.SERVICE_KEY,
                        },
                        {
                            key: "status",
                            label: "Booking status",
                            type: "select",
                            options: BOOKING_FILTER_FIELDS_OPTIONS.BOOKING_STATUS,
                        },
                        {
                            key: "payment_status",
                            label: "Payment status",
                            type: "select",
                            options: BOOKING_FILTER_FIELDS_OPTIONS.PAYMENT_STATUS,
                        },
                        { key: "date_from", label: "Preferred date from", type: "date" },
                        { key: "date_to", label: "Preferred date to", type: "date" },
                    ]}
                    value={filters}
                    onChange={setFilters}
                    onExport={exportRows}
                    resultCount={filtered.length}
                    totalCount={bookings.length}
                />
            </div>
            <div className="card-soft mt-4 overflow-x-auto p-4">
                <Table data-testid="bookings-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead>Service</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Date / Slot</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Payment</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.map((b) => (
                            <TableRow key={b.id} data-testid={`booking-row-${b.reference_id}`}>
                                <TableCell className="font-mono text-xs">
                                    {b.reference_id}
                                </TableCell>
                                <TableCell>
                                    <p className="font-medium">{b.service_label}</p>
                                    <p className="text-xs text-[#475569]">
                                        {b.amount
                                            ? `$${b.amount} ${b.currency?.toUpperCase()}`
                                            : "—"}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    <p className="font-medium">{b.full_name}</p>
                                    <p className="text-xs text-[#475569]">{b.email}</p>
                                    <p className="text-xs text-[#475569]">{b.phone}</p>
                                </TableCell>
                                <TableCell className="text-sm">
                                    {b.preferred_date}
                                    <br />
                                    <span className="text-xs text-[#475569]">
                                        {b.preferred_time}
                                    </span>
                                    {b.meet_link && (
                                        <a
                                            href={b.meet_link}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            data-testid={`meet-link-${b.reference_id}`}
                                            className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                                            title={b.meet_link}
                                        >
                                            ▶ Meet
                                        </a>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <StatusDropdown booking={b} onChanged={onChanged} />
                                </TableCell>
                                <TableCell>
                                    <PaymentBadge status={b.payment_status} />
                                </TableCell>
                                <TableCell className="text-right">
                                    <ReminderButton booking={b} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="py-8 text-center text-sm text-[#475569]">
                                    No bookings match these filters.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}
